/* ====================================================================
   NSE Collection — assets/nse-collection.js
   Handles: card Add-to-Cart, variant picker, image carousels,
   filter drawer, sort sheet, load more. Cooperates with window.NseBag
   and window.NseWishlist. Suppresses PCD auto-drawer on this template.
   ==================================================================== */
(function () {
  'use strict';

  var NSE = {
    _hasNseRoot: false,

    init: function () {
      this._hasNseRoot = !!document.querySelector('[data-nse-root]');
      this.patchPCD();
      this.bindAdds();
      this.bindImageCarousels();
      this.bindFilters();
      this.bindSort();
      this.bindLoadMore();
      this.bindEsc();
      this.bindHeroD();
      this.bindVariantPicker();
    },

    /* ---------- Suppress PCD auto-drawer on this template -----------
       PCD opens its cart drawer after /cart/add. On the collection
       page we want the add to quietly update the bag widget instead.
       We monkey-patch window.PCD.openDrawer: it's only allowed through
       when an NSE element (bag widget / cart icon) explicitly sets
       window._nseAllowDrawer = true. */
    patchPCD: function () {
      if (!this._hasNseRoot) return;
      var attempts = 0;
      function tryPatch() {
        if (window._nsePCDPatched) return true;
        if (!window.PCD || typeof window.PCD.openDrawer !== 'function') return false;

        var orig = window.PCD.openDrawer.bind(window.PCD);
        window._nseOrigOpenDrawer = orig;
        window.PCD.openDrawer = function (opts) {
          if (window._nseAllowDrawer) {
            window._nseAllowDrawer = false;
            return orig(opts);
          }
          // Swallow automatic open on this template
        };
        window._nsePCDPatched = true;
        return true;
      }

      if (tryPatch()) return;
      var iv = setInterval(function () {
        attempts++;
        if (tryPatch() || attempts > 40) clearInterval(iv); // try for ~4s
      }, 100);
    },

    /* ---------- Add to Cart (intercepts in CAPTURE phase) ---------- */
    bindAdds: function () {
      var self = this;
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-nse-add]');
        if (!btn || btn.disabled) return;

        e.preventDefault();
        e.stopPropagation();

        var card = btn.closest('.nse-card');
        var handle = card ? card.dataset.productHandle : null;
        var variantCount = parseInt(btn.dataset.variantCount || '1', 10);

        if (variantCount > 1 && handle) {
          self.openVariantPicker(handle, btn);
          return;
        }

        var variantId = btn.dataset.variantId;
        if (!variantId) return;
        self.performAdd(variantId, btn);
      }, true); // capture phase
    },

    performAdd: function (variantId, btn) {
      var self = this;

      var vid = parseInt(variantId, 10);
      if (!vid || isNaN(vid)) {
        self.toast('This product has no variant to add.', 'error');
        return;
      }

      // Cache the button's original HTML on the element itself so repeat
      // clicks don't accumulate spinners or lose the label.
      if (!btn.dataset.nseOrig) btn.dataset.nseOrig = btn.innerHTML;

      // ---- Optimistic UI — user sees "Added" with zero latency ----
      btn.classList.add('is-added');
      btn.innerHTML = '<span class="nse-card__add-check" aria-hidden="true">✓</span> Added';
      btn.disabled = false;

      // Optimistically nudge the bag widget too (count +1, un-hide).
      if (window.NseBag && typeof window.NseBag.optimisticIncrement === 'function') {
        window.NseBag.optimisticIncrement();
      }

      // ---- Actual add (single round-trip, drawer HTML returned inline) ----
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-PCD-Own': '1'
        },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify({
          items: [{ id: vid, quantity: 1 }],
          sections: 'premium-cart-drawer',
          sections_url: window.location.pathname
        })
      })
      .then(function (r) {
        return r.json().then(function (body) {
          if (!r.ok) {
            var m = (body && (body.description || body.message)) || ('Add failed (' + r.status + ')');
            throw new Error(m);
          }
          return body;
        });
      })
      .then(function (resp) {
        // Drawer is now current from the returned sections HTML
        if (window.NseBag && typeof window.NseBag.patchDrawerFromSections === 'function') {
          window.NseBag.patchDrawerFromSections(resp);
        }
        // Reconcile widget with real cart state (runs in background)
        if (window.NseBag && typeof window.NseBag.refreshFromCart === 'function') {
          window.NseBag.refreshFromCart();
        }
      })
      .catch(function (err) {
        console.error('[NSE] add failed', err);
        self.toast((err && err.message) || 'Could not add to cart', 'error');
        // Roll back the optimistic UI
        btn.classList.remove('is-added');
        btn.innerHTML = btn.dataset.nseOrig || 'Add';
        btn.disabled = false;
        // Sync widget back to true state
        if (window.NseBag && typeof window.NseBag.refreshFromCart === 'function') {
          window.NseBag.refreshFromCart();
        }
      });
    },

    /* Lightweight toast. Stacks multiple, auto-dismisses after 3.5s. */
    toast: function (message, kind) {
      var host = document.getElementById('nse-toast-host');
      if (!host) {
        host = document.createElement('div');
        host.id = 'nse-toast-host';
        document.body.appendChild(host);
      }
      var t = document.createElement('div');
      t.className = 'nse-toast' + (kind ? ' nse-toast--' + kind : '');
      t.textContent = message;
      host.appendChild(t);
      requestAnimationFrame(function () { t.classList.add('is-visible'); });
      setTimeout(function () {
        t.classList.remove('is-visible');
        setTimeout(function () { t.remove(); }, 260);
      }, 3500);
    },

    /* ---------- Variant picker (compact centered modal) ---------- */
    bindVariantPicker: function () {
      if (this._vpBuilt) return;
      this._vpBuilt = true;

      var modal = document.createElement('div');
      modal.className = 'nse-vp-modal';
      modal.id = 'nse-variant-picker';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-hidden', 'true');
      modal.innerHTML =
        '<div class="nse-vp-modal__backdrop" data-nse-vp-close></div>' +
        '<div class="nse-vp-modal__panel" role="document">' +
          '<header class="nse-vp-modal__header">' +
            '<h3 data-nse-vp-title>Choose an option</h3>' +
            '<button type="button" class="nse-vp-modal__close" data-nse-vp-close aria-label="Close">' +
              '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
            '</button>' +
          '</header>' +
          '<div class="nse-vp-modal__body"><div class="nse-variant-list" data-nse-variant-list></div></div>' +
        '</div>';
      document.body.appendChild(modal);

      var self = this;
      modal.addEventListener('click', function (e) {
        if (e.target.closest('[data-nse-vp-close]')) self.closeVariantPicker();
      });
    },

    openVariantPicker: function (handle, sourceBtn) {
      var self = this;
      var modal = document.getElementById('nse-variant-picker');
      if (!modal) return;
      self._vpSourceBtn = sourceBtn;

      var list = modal.querySelector('[data-nse-variant-list]');
      var title = modal.querySelector('[data-nse-vp-title]');
      list.innerHTML = '<div style="padding:20px;text-align:center;color:var(--nse-muted);">Loading…</div>';
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (product) {
          if (title) title.textContent = 'Choose · ' + product.title;
          list.innerHTML = '';

          product.variants.forEach(function (v) {
            var img = (v.featured_image && v.featured_image.src) || product.featured_image || '';
            var price = Math.round(v.price / 100).toLocaleString('en-IN');
            var compare = v.compare_at_price && v.compare_at_price > v.price
              ? Math.round(v.compare_at_price / 100).toLocaleString('en-IN')
              : '';
            var soldOut = !v.available;

            var row = document.createElement('button');
            row.type = 'button';
            row.className = 'nse-variant-item' + (soldOut ? ' is-sold-out' : '');
            row.disabled = soldOut;
            row.dataset.variantId = v.id;
            row.innerHTML =
              '<span class="nse-variant-item__img">' +
                (img
                  ? '<img src="' + self.escape(img) + '" alt="' + self.escape(v.title) + '">'
                  : '<span class="nse-variant-item__img-ph">💎</span>') +
              '</span>' +
              '<span class="nse-variant-item__info">' +
                '<span class="nse-variant-item__title">' + self.escape(v.title) + '</span>' +
                '<span class="nse-variant-item__price">' +
                  '₹' + price +
                  (compare ? ' <s>₹' + compare + '</s>' : '') +
                '</span>' +
              '</span>' +
              '<span class="nse-variant-item__cta">' + (soldOut ? 'Sold' : 'Add') + '</span>';

            row.addEventListener('click', function () {
              if (soldOut) return;
              row.disabled = true;
              var cta = row.querySelector('.nse-variant-item__cta');
              cta.textContent = '✓';

              // Optimistic UI on source button + widget
              if (self._vpSourceBtn) {
                if (!self._vpSourceBtn.dataset.nseOrig) {
                  self._vpSourceBtn.dataset.nseOrig = self._vpSourceBtn.innerHTML;
                }
                self._vpSourceBtn.classList.add('is-added');
                self._vpSourceBtn.innerHTML = '<span class="nse-card__add-check" aria-hidden="true">✓</span> Added';
                self._vpSourceBtn.disabled = false;
              }
              if (window.NseBag && typeof window.NseBag.optimisticIncrement === 'function') {
                window.NseBag.optimisticIncrement();
              }
              setTimeout(function () { self.closeVariantPicker(); }, 300);

              fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'X-PCD-Own': '1'
                },
                credentials: 'same-origin',
                cache: 'no-store',
                body: JSON.stringify({
                  items: [{ id: v.id, quantity: 1 }],
                  sections: 'premium-cart-drawer',
                  sections_url: window.location.pathname
                })
              })
              .then(function (r) {
                return r.json().then(function (body) {
                  if (!r.ok) {
                    var m = (body && (body.description || body.message)) || 'Could not add';
                    throw new Error(m);
                  }
                  return body;
                });
              })
              .then(function (resp) {
                if (window.NseBag && typeof window.NseBag.patchDrawerFromSections === 'function') {
                  window.NseBag.patchDrawerFromSections(resp);
                }
                if (window.NseBag && typeof window.NseBag.refreshFromCart === 'function') {
                  window.NseBag.refreshFromCart();
                }
              })
              .catch(function (err) {
                console.error('[NSE] variant add failed', err);
                self.toast((err && err.message) || 'Could not add', 'error');
                if (self._vpSourceBtn) {
                  self._vpSourceBtn.classList.remove('is-added');
                  self._vpSourceBtn.innerHTML = self._vpSourceBtn.dataset.nseOrig || 'Add';
                  self._vpSourceBtn.disabled = false;
                }
                if (window.NseBag && typeof window.NseBag.refreshFromCart === 'function') {
                  window.NseBag.refreshFromCart();
                }
              });
            });

            list.appendChild(row);
          });
        })
        .catch(function (err) {
          console.error('[NSE] variant picker load failed', err);
          list.innerHTML = '<div style="padding:20px;text-align:center;color:var(--nse-muted);">Could not load variants. <a href="#" onclick="window.location.reload();return false;">Retry</a></div>';
        });
    },

    closeVariantPicker: function () {
      var modal = document.getElementById('nse-variant-picker');
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this._vpSourceBtn = null;
    },

    /* ---------- Image carousel dots (and clickable dots) ---------- */
    bindImageCarousels: function () {
      document.querySelectorAll('[data-nse-img-carousel]').forEach(function (carousel) {
        if (carousel._nseBound) return;
        carousel._nseBound = true;
        var dotsEl = carousel.nextElementSibling;
        if (!dotsEl || !dotsEl.classList.contains('nse-card__dots')) return;
        var dots = dotsEl.querySelectorAll('.nse-card__dot');
        if (!dots.length) return;

        // Sync dot on scroll
        carousel.addEventListener('scroll', function () {
          var idx = Math.round(carousel.scrollLeft / carousel.offsetWidth);
          dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
        }, { passive: true });

        // Clickable dots — since horizontal swipe is disabled (touch-action
        // pan-y) so that vertical page scrolling wins, the dots are now the
        // primary way to switch images on mobile.
        dots.forEach(function (d, i) {
          d.style.cursor = 'pointer';
          d.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            carousel.scrollTo({ left: carousel.offsetWidth * i, behavior: 'smooth' });
          });
        });
      });
    },

    /* ---------- Filters ---------- */
    bindFilters: function () {
      var self = this;
      document.addEventListener('click', function (e) {
        var openBtn = e.target.closest('[data-nse-open-filters]');
        if (openBtn) {
          e.preventDefault();
          self.openFilterDrawer(openBtn.getAttribute('data-nse-open-filters'));
          return;
        }
        if (e.target.closest('[data-nse-close-filters]')) { self.closeFilterDrawer(); return; }
        if (e.target.closest('[data-nse-clear-filters]')) { e.preventDefault(); self.clearFilters(); return; }
        if (e.target.closest('[data-nse-apply-filters]')) { e.preventDefault(); self.applyFilters(); return; }

        var pill = e.target.closest('.nse-variant-pill');
        if (pill && !pill.classList.contains('is-sold-out') && !pill.disabled) {
          e.preventDefault();
          self.selectVariant(pill);
        }
      });
    },

    openFilterDrawer: function (scrollToParam) {
      var drawer = document.getElementById('nse-filter-drawer');
      if (!drawer) return;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (scrollToParam) {
        var target = drawer.querySelector('[data-filter-param="' + scrollToParam + '"]');
        if (target) setTimeout(function () { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 320);
      }
    },

    closeFilterDrawer: function () {
      var drawer = document.getElementById('nse-filter-drawer');
      if (drawer) {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
      }
      document.body.style.overflow = '';
    },

    applyFilters: function () {
      var form = document.querySelector('[data-nse-filter-form]');
      if (!form) return;
      var params = new URLSearchParams();
      var currentUrl = new URL(window.location.href);
      var currentSort = currentUrl.searchParams.get('sort_by');
      if (currentSort) params.set('sort_by', currentSort);
      form.querySelectorAll('input').forEach(function (input) {
        if (!input.name) return;
        if (input.type === 'checkbox' && input.checked) params.append(input.name, input.value || '1');
        else if ((input.type === 'number' || input.type === 'text') && input.value !== '') params.set(input.name, input.value);
      });
      var newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      this.closeFilterDrawer();
      this.fetchAndSwap(newUrl);
    },

    clearFilters: function () {
      var url = new URL(window.location.href);
      var sort = url.searchParams.get('sort_by');
      var clean = sort ? window.location.pathname + '?sort_by=' + encodeURIComponent(sort) : window.location.pathname;
      this.closeFilterDrawer();
      this.fetchAndSwap(clean);
    },

    fetchAndSwap: function (url) {
      var self = this;
      var grid = document.querySelector('[data-nse-grid]');
      if (grid) grid.style.opacity = '0.45';
      return fetch(url, { headers: { 'X-Requested-With': 'fetch' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.querySelector('[data-nse-grid]');
          var curGrid = document.querySelector('[data-nse-grid]');
          if (newGrid && curGrid) curGrid.innerHTML = newGrid.innerHTML;

          var newCount = doc.querySelector('.nse-result-count');
          var curCount = document.querySelector('.nse-result-count');
          if (newCount && curCount) curCount.innerHTML = newCount.innerHTML;

          var newChips = doc.querySelector('.nse-filter-row');
          var curChips = document.querySelector('.nse-filter-row');
          if (newChips && curChips) curChips.innerHTML = newChips.innerHTML;

          var newAll = doc.querySelector('.nse-filter-chip--all');
          var curAll = document.querySelector('.nse-filter-chip--all');
          if (newAll && curAll) curAll.innerHTML = newAll.innerHTML;

          var newPag = doc.querySelector('.nse-pagination');
          var curPag = document.querySelector('.nse-pagination');
          if (newPag && curPag) curPag.outerHTML = newPag.outerHTML;
          else if (newPag && curGrid) curGrid.insertAdjacentHTML('afterend', newPag.outerHTML);
          else if (!newPag && curPag) curPag.remove();

          var newDrawer = doc.querySelector('#nse-filter-drawer');
          var curDrawer = document.querySelector('#nse-filter-drawer');
          if (newDrawer && curDrawer) {
            var body = newDrawer.querySelector('[data-nse-filter-form]');
            var curBody = curDrawer.querySelector('[data-nse-filter-form]');
            if (body && curBody) curBody.innerHTML = body.innerHTML;
          }

          // Rehydrate sort sheet so the checked radio + active state
          // reflect the new sort_by.
          var newSort = doc.querySelector('#nse-sort-drawer');
          var curSort = document.querySelector('#nse-sort-drawer');
          if (newSort && curSort) {
            var newSortBody = newSort.querySelector('.nse-sort-list');
            var curSortBody = curSort.querySelector('.nse-sort-list');
            if (newSortBody && curSortBody) curSortBody.innerHTML = newSortBody.innerHTML;
          }

          // Also update the filter bar's sort button label
          var newSortBtn = doc.querySelector('[data-nse-open-sort]');
          var curSortBtn = document.querySelector('[data-nse-open-sort]');
          if (newSortBtn && curSortBtn) curSortBtn.innerHTML = newSortBtn.innerHTML;

          // Use replaceState (not pushState) so browser-back returns the user
          // to where they actually came from (product page, homepage, etc.)
          // instead of walking through every filter/sort tweak they made.
          history.replaceState({}, '', url);
          if (grid) grid.style.opacity = '';
          if (window.NseWishlist && typeof window.NseWishlist.hydrateHearts === 'function') {
            window.NseWishlist.hydrateHearts();
          }
          self.bindImageCarousels();
        })
        .catch(function (e) {
          console.error('[NSE] filter fetch failed', e);
          window.location.href = url;
        });
    },

    /* ---------- Sort ---------- */
    bindSort: function () {
      var self = this;
      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-nse-open-sort]')) {
          e.preventDefault();
          var s = document.getElementById('nse-sort-drawer');
          if (s) { s.classList.add('is-open'); s.setAttribute('aria-hidden', 'false'); document.body.style.overflow = 'hidden'; }
          return;
        }
        if (e.target.closest('[data-nse-close-sort]')) { self.closeSort(); return; }
      });
      document.addEventListener('change', function (e) {
        var radio = e.target.closest('[data-nse-sort-option]');
        if (!radio) return;
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', radio.value);
        self.closeSort();
        self.fetchAndSwap(url.toString());
      });
    },

    closeSort: function () {
      var s = document.getElementById('nse-sort-drawer');
      if (s) { s.classList.remove('is-open'); s.setAttribute('aria-hidden', 'true'); }
      document.body.style.overflow = '';
    },

    /* ---------- Load more ---------- */
    bindLoadMore: function () {
      var self = this;
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-nse-load-more]');
        if (!btn) return;
        e.preventDefault();
        var url = btn.dataset.nextUrl;
        if (!url) return;
        var original = btn.innerHTML;
        btn.disabled = true;
        btn.innerHTML = 'Loading…';
        fetch(url)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var cards = doc.querySelectorAll('.nse-card');
            var grid = document.querySelector('[data-nse-grid]');
            if (!grid) throw new Error('grid missing');
            cards.forEach(function (c) { grid.appendChild(c); });
            var nextBtn = doc.querySelector('[data-nse-load-more]');
            if (nextBtn) {
              btn.dataset.nextUrl = nextBtn.dataset.nextUrl;
              btn.innerHTML = nextBtn.innerHTML;
              btn.disabled = false;
            } else {
              btn.remove();
            }
            if (window.NseWishlist) window.NseWishlist.hydrateHearts();
            self.bindImageCarousels();
          })
          .catch(function (e) {
            console.error('[NSE] load more failed', e);
            btn.innerHTML = 'Retry';
            btn.disabled = false;
            setTimeout(function () { btn.innerHTML = original; }, 1500);
          });
      });
    },

    /* ---------- Variant pill (Frido) ---------- */
    selectVariant: function (pill) {
      var card = pill.closest('.nse-card');
      if (!card) return;
      card.querySelectorAll('.nse-variant-pill').forEach(function (p) { p.classList.remove('is-active'); });
      pill.classList.add('is-active');
      var variantId = pill.dataset.variantId;
      var price = parseInt(pill.dataset.variantPrice, 10);
      var compare = pill.dataset.variantCompare ? parseInt(pill.dataset.variantCompare, 10) : 0;
      var priceEl = card.querySelector('[data-price]');
      var wasEl = card.querySelector('.nse-card__was');
      var offEl = card.querySelector('.nse-card__off');
      if (priceEl && !isNaN(price)) priceEl.textContent = '₹' + Math.round(price / 100).toLocaleString('en-IN');
      if (compare > price && !isNaN(price)) {
        var pct = Math.round((compare - price) * 100 / compare);
        if (wasEl) { wasEl.textContent = '₹' + Math.round(compare / 100).toLocaleString('en-IN'); wasEl.hidden = false; }
        if (offEl) { offEl.textContent = pct + '% OFF'; offEl.hidden = false; }
      } else {
        if (wasEl) wasEl.hidden = true;
        if (offEl) offEl.hidden = true;
      }
      var addBtn = card.querySelector('[data-nse-add]');
      if (addBtn) addBtn.dataset.variantId = variantId;
    },

    /* ---------- Hero D indicator dots ---------- */
    bindHeroD: function () {
      var wrap = document.querySelector('[data-nse-hero-d]');
      if (!wrap) return;
      var carousel = wrap.querySelector('.nse-hero-d__carousel');
      var dots = wrap.querySelectorAll('.nse-hero-d__ind');
      if (!carousel || !dots.length) return;
      carousel.addEventListener('scroll', function () {
        var idx = Math.round(carousel.scrollLeft / carousel.offsetWidth);
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
      }, { passive: true });
    },

    bindEsc: function () {
      var self = this;
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          self.closeFilterDrawer();
          self.closeSort();
          self.closeVariantPicker();
        }
      });
    },

    escape: function (s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
  };

  window.NSE = NSE;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { NSE.init(); });
  } else {
    NSE.init();
  }
})();
